import { observable, action, computed, makeObservable } from 'mobx';
import axios from '../lib/axios';
import agent from '../lib/agent';
import conf from '../conf';

class Store {
  user = null;
  groupMembers = [];
  notifications = [];
  status = 'loading';

  constructor() {
    makeObservable(this, {
      user: observable,
      groupMembers: observable,
      notifications: observable,
      status: observable,
      loggedIn: computed,
      isAdmin: computed,
      avatar: computed,
      isActivated: computed,
      isOnboardingCompleted: computed,
      login: action,
      hasAccess: action,
      logout: action,
      addNotification: action,
      fetchCredentials: action,
      switchGroup: action,
      fetchGroupMembers: action,
      deleteGroup: action,
      removeGroupMember: action
    });

    this.fetchCredentials();
  }

  get loggedIn() {
    return Boolean(this.user && this.user.logged_in);
  }

  get isAdmin() {
    return Boolean(this.user?.is_admin);
  }

  get avatar() {
    return `${conf.E2E_API_URL}user/${this.user?.id}/avatar?${this.user?.last_active}`;
  }

  get isActivated() {
    return Boolean(this.user?.is_activated);
  }

  get isOnboardingCompleted() {
    return Boolean(this.user?.is_onboarding_completed);
  }

  loginWithCode = async code => {
    const { data } = await axios.post('/login', {
      code
    });

    if (data.ok !== 1) return false;

    await this.fetchCredentials();
    return true;
  };

  login = async (email, password) => {
    const { data } = await axios.post('/login', {
      email,
      password
    });

    if (data.ok !== 1) return false;

    await this.fetchCredentials();
    return true;
  };

  hasAccess(id) {
    return this.user.group?.menu_items.includes(id);
  }

  logout = async () => {
    localStorage.removeItem('jwt');
    await agent.User.logout();
    this.user = null;
  };

  addNotification(notification) {
    this.notifications = [...this.notifications, notification];
  }

  fetchCredentials = async () => {
    const { data } = await axios.get('/status');
    this.user = data;
    if (data.msg === 'UNDER_MAINTENANCE') {
      this.status = 'UNDER_MAINTENANCE';
    } else {
      this.status = 'loaded';
    }

    if (data.is_activated && data.logged_in && data.group) {
      this.fetchGroupMembers();
    }

    return data;
  };

  hasGroupPermission = permission => {
    return Boolean(this.user.group.permissions[permission]);
  };

  switchGroup = async id => {
    try {
      const { data } = await axios.get(`/group/${id}/switch`);
      await this.fetchCredentials();
      this.fetchGroupMembers();
      return data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      return null;
    }
  };

  fetchGroupMembers = async () => {
    const { data } = await axios.get(
      `/group/${this.user.group.id}/relationships/users`
    );
    this.groupMembers = data.data;
    return data.data;
  };

  deleteGroup = async groupId => {
    const newGroups = [...this.user.groups].filter(
      group => group.id !== groupId
    );
    this.user = {
      ...this.user,
      groups: newGroups
    };
    await agent.Group.delete(groupId);
  };

  removeGroupMember = async userId => {
    const groupId = this.user.group.id;
    const idx = this.groupMembers.findIndex(m => m.id === userId);
    this.groupMembers.splice(idx, 1);
    await axios.delete(`/group/${groupId}/relationships/users`, {
      data: [
        {
          id: userId
        }
      ]
    });
  };
}

const store = new Store();
export default store;
